import { ItemTypeEntity, ItemTypeFragment, RawItemType } from './Types';
import { ItemEntity } from '../Item/Types';
import { ItemModel } from '../Item/ItemModel';
import { BaseModel } from '../BaseModel/BaseModel';
import { GalleryEntity } from '../Gallery/Types';
import { GalleryModel } from '../Gallery/GalleryModel';
import { ProjectAddItemItemTypePayload } from '../../Processes/Gateways/CustomProjects/Interfaces/ProjectAddItemPayload';
import { ProjectItemTypeOrderedQtyPayload } from '../../Processes/Gateways/CustomProjects/Interfaces/ProjectItemOrderedQtyPayload';

export class ItemTypeModel
    extends BaseModel<RawItemType, ItemTypeEntity>
    implements ItemTypeEntity
{
    createdAt: string = new Date().toISOString();
    data: any | null = null;
    deletedAt: string | null = null;
    id: number = 0;
    items?: ItemEntity[];
    labor: boolean = false;
    orderedQty: number = 0;
    percentage: number = 0;
    qty: number = 0;
    qtyFormula: string = '';
    slug: string | null = null;
    title: string = '';
    tool: boolean = false;
    unit: string = '';
    fairMarketPrice: string | null = null;
    updatedAt: string = new Date().toISOString();
    index?: number;
    fragments?: ItemTypeFragment[];
    galleries?: GalleryEntity[];

    constructor(data?: RawItemType) {
        super();
        if (data) {
            this.initialize(data);
        }
    }

    initialize(data: RawItemType) {
        this.createdAt = data.created_at;
        this.deletedAt = data.deleted_at;
        this.id = data.id;
        this.labor = data.labor;
        this.orderedQty = data.ordered_qty;
        this.percentage = data.percentage;
        this.qty = data.qty;
        this.qtyFormula = data.qty_formula;
        this.slug = data.slug;
        this.title = data.title;
        this.tool = data.tool;
        this.unit = data.unit;
        this.updatedAt = data.updated_at;
        this.index = data.index ? data.index : this.index;
        this.fairMarketPrice = data.fair_market_price;

        this.items = data.items
            ? data.items.map(item => new ItemModel(item))
            : this.items;
        this.fragments = data.fragments ? data.fragments : this.fragments;
        this.galleries = data.galleries?.length
            ? data.galleries.map(gallery => new GalleryModel(gallery))
            : this.galleries;
    }

    parse(): RawItemType {
        return {
            created_at: this.createdAt,
            data: this.data,
            deleted_at: this.deletedAt,
            id: this.id,
            labor: this.labor,
            ordered_qty: this.orderedQty,
            percentage: this.percentage,
            qty: this.qty,
            qty_formula: this.qtyFormula,
            slug: this.slug,
            title: this.title,
            tool: this.tool,
            unit: this.unit,
            updated_at: this.updatedAt,
            fair_market_price: this.fairMarketPrice,
            ...(this.index && { index: this.index }),
            ...(this.items && { items: this.items.map(item => item.parse()) }),
            ...(this.fragments && { fragments: this.fragments }),
            ...(this.galleries && {
                galleries: this.galleries.map(gallery => gallery.parse()),
            }),
        };
    }

    updateItems(items: ItemEntity[]) {
        this.items = items;
    }

    getAddItemPayload(): ProjectAddItemItemTypePayload {
        return {
            item_type_id: this.id,
            item_type_index: this.index ?? 0,
            item_ids: this.items?.length
                ? this.items?.map(item => item.getAddItemPayload())
                : [],
        };
    }

    getOrderedQtyPayload(): ProjectItemTypeOrderedQtyPayload[] {
        return (
            this.items?.map(item => ({
                ...item.getOrderedQtyPayload(),
                item_type_id: this.id,
                item_type_index: this.index ?? 0,
            })) ?? []
        );
    }

    updateQuantity(quantity: number) {
        this.qty = quantity;
    }
}
